// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CartLineContainerStyles } from "@awc/App/Components/Cart/CartLineContainer";
import { CHECKOUT_BREAKPOINT } from "@awc/XDL/constants";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { color, xScreen } from "@awc/XDL/Utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface CheckoutSharedStyles {
    sectionTitle?: InjectableCss;
    sectionInnerContainer?: InjectableCss;
    cartlineContainer?: CartLineContainerStyles;
    orderSummaryTitle?: InjectableCss;
}

const sharedTitleText: InjectableCss = {
    css: css`
        background: ${color("primary.main")};
        color: ${color("common.ui1")};
        font-size: ${fontConfig.fontSize.link}px;
        line-height: ${fontConfig.lineHeight.regular};
        padding: 20px 12px;
        font-weight: ${fontConfig.weight.bold};
        width: 100%;
        display: block;
        ${xScreen("lte", CHECKOUT_BREAKPOINT)} {
            color: ${color("primary.main")};
            background: ${color("common.ui1")};
            padding: 0;
            margin-bottom: 12px;
        }
    `,
};

const checkoutSharedStyles: CheckoutSharedStyles = {
    sectionTitle: {
        ...sharedTitleText,
    },
    cartlineContainer: {
        container: {
            css: css`
                display: grid;
                grid-template-columns: 114px minmax(400px, 1fr) auto auto;
                ${xScreen("lte", "sm")} {
                    grid-template-columns: 1fr auto auto;
                    grid-column-gap: 16px;
                }
            `,
        },
        image: {
            css: css`
                grid-column: 1 / 2;
                grid-row: 1 / 5;
                ${xScreen("lte", "sm")} {
                    grid-column: 1/ 4;
                    display: flex;
                    justify-content: center;
                }
                @media print {
                    min-width: 120px;
                    min-height: 120px;
                    margin-bottom: 0;
                    float: left;
                }
            `,
        },

        description: {
            css: css`
                grid-column: 2 / 3;
                grid-row: 1 / 2;
                position: relative;
                margin-bottom: 0;
                display: flex;
                flex-direction: column;

                ${xScreen("lte", "sm")} {
                    grid-column: 1/ 4;
                    grid-row: 2 / 3;
                }
                @media print {
                    min-height: 120px;
                    display: block;
                }
            `,
        },

        price: {
            css: css`
                grid-column: 2 / 3;
                grid-row: 2 / 3;
                ${xScreen("lte", "sm")} {
                    grid-column: 1 / 4;
                    grid-row: 3 / 4;
                }
                @media print {
                    display: flex;
                    align-items: center;
                    min-width: 30%;
                    min-height: 50px;
                    float: left;
                }
            `,
        },

        qty: {
            css: css`
                text-align: right;
                grid-column: 3 / 4;
                grid-row: 4 / 5;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding-right: 4px;
                ${xScreen("lte", "sm")} {
                    grid-row: 5 / 6;
                    grid-column: 2 / 3;
                }
                @media print {
                    margin-right: 0;
                    min-width: 30%;
                    min-height: 50px;
                    float: left;
                }
            `,
        },
        total: {
            css: css`
                display: flex;
                justify-content: flex-end;
                align-items: center;
                grid-column: 4 / 5;
                grid-row: 4 / 5;
                &:empty {
                    display: none;
                }
                ${xScreen("lte", "sm")} {
                    grid-row: 5 / 6;
                    grid-column: 3 / 4;
                }

                @media print {
                    min-width: 30%;
                    min-height: 50px;
                    float: right;
                }
            `,
        },

        actions: {
            css: css`
                grid-column: 2 / 3;
                grid-row: 3 / 4;
                position: relative;
                display: flex;

                ${xScreen("lte", "sm")} {
                    grid-column: 1 / 4;
                    grid-row: 4 / 5;
                    gap: 12px;
                }

                @media print {
                    display: none;
                }

                &:empty {
                    display: none;
                }
            `,
        },
        notes: {
            css: css`
                grid-column: 2 / 3;
                grid-row: 4 / 5;
                position: relative;

                &:empty {
                    display: none;
                }
                ${xScreen("lte", "sm")} {
                    grid-column: 1 / 4;
                    grid-row: 6 / 7;
                    padding-left: 0;
                }

                @media print {
                    min-width: 100%;
                    float: left;
                }
            `,
        },
    },
    sectionInnerContainer: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-top: 0;
            padding: 12px;
            ${xScreen("lte", CHECKOUT_BREAKPOINT)} {
                border: none;
                padding: 0;
            }
        `,
    },
    orderSummaryTitle: {
        css: css`
            ${sharedTitleText?.css}
            ${xScreen("lte", CHECKOUT_BREAKPOINT)} {
                background: ${color("common.ui1")};
            }
        `,
    },
};

export default checkoutSharedStyles;
